/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-05 13:58:05
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import type {
  ShopifyIntegrationSettingsAction,
  ShopifyIntegrationSettingsState
} from 'stores/flow-type/shopify-integration/shopify-integration-settings.type';

import {
  UPDATE_FIELD,
  UPDATE_MULTI_FIELDS,
  CHANGE_ACTIVE_MENU_ITEM,
  CHANGE_SELECTED_PAGE,
  UPDATE_MENU_SETTING,
  REMOVE_ELEMENT,
  UPDATE_WHOLE_MENU_SETTING,
  TOGGLE_EXPAND_FULLSCREEN,
  GET_THEME_SETTING_BY_ID_REQUEST,
  GET_THEME_SETTING_BY_ID_SUCCESS,
  GET_THEME_SETTING_BY_ID_FAILURE,
  GET_THEME_SETTING_DEFAULT_VALUE_BY_ID_REQUEST,
  GET_THEME_SETTING_DEFAULT_VALUE_BY_ID_SUCCESS,
  GET_THEME_SETTING_DEFAULT_VALUE_BY_ID_FAILURE,
  UPDATE_THEME_SETTING,
  EDIT_THEME_SETTING_BY_ID_REQUEST,
  EDIT_THEME_SETTING_BY_ID_SUCCESS,
  EDIT_THEME_SETTING_BY_ID_FAILURE,
  CHANGE_DEVICE
} from 'stores/flow-type/shopify-integration/shopify-integration-settings.type';
import { TYPE_UPDATE_SETTING, DEVICE, SIDEBAR_MENU } from 'src/constants/theme-settings';
import { flattenMenu } from 'src/helpers/theme-settings-helper';

const initialState: ShopifyIntegrationSettingsState = {
  themeName: '',
  activeMenuItem: '',
  selectedPage: {},
  updateType: '', // Type of menu (collection | search | common)
  linkThemeIds: [],
  developerMode: false,
  enableCustomization: false,
  codeEditTemplate: {}, // code editor
  customizeTemplate: '',
  previewData: {},
  templatesEditing: {},

  templateSetting: null, // Save for initialValues of Redux form
  templateSettingLoading: false,
  templateSettingSuccess: false,
  templateSettingError: undefined,

  themeSettingDefaultValue: null, // Default value of matching theme
  themeSettingDefaultThemeValue: null, // For default theme value (always have)
  themeSettingDefaultValueLoading: false,
  themeSettingDefaultValueSuccess: false,
  themeSettingDefaultValueError: undefined,

  editThemeSettingLoading: false,
  editThemeSettingSuccess: false,
  editThemeSettingError: undefined,

  isFormReplaceDirty: false, // Use for enable button save when re-initialize form
  firstTimeActivate: false,
  previewSetting: {},
  device: DEVICE.DESKTOP,
  labelTranslation: {},
  boostThemeLib: 'default',
  boostThemeLibVersion: 'latest',
  boostThemeLibName: 'default',

  themeLibTemplate: '',
  originThemeLibTemplate: '',

  sideBarMenu: SIDEBAR_MENU.STYLE
};

const themeSettingReducers = (
  state: ShopifyIntegrationSettingsState = initialState,
  action: ShopifyIntegrationSettingsAction
) => {
  switch (action.type) {
    case UPDATE_FIELD: {
      const { fieldName, data } = action?.payload;
      return {
        ...state,
        [fieldName]: data
      };
    }

    case UPDATE_MULTI_FIELDS: {
      const { data } = action?.payload;
      return {
        ...state,
        ...data
      };
    }

    case CHANGE_ACTIVE_MENU_ITEM: {
      const { id, updateType } = action?.payload;

      return {
        ...state,
        updateType,
        activeMenuItem: id
      };
    }
    case CHANGE_SELECTED_PAGE: {
      const { payload } = action;
      return {
        ...state,
        selectedPage: payload
      };
    }
    case UPDATE_MENU_SETTING: {
      const { id, children, updateType } = action?.payload;
      const cloneState = _.cloneDeep(state);
      const mapMenuByType = {
        [TYPE_UPDATE_SETTING.COLLECTION]: cloneState.collectionSetting,
        [TYPE_UPDATE_SETTING.SEARCH]: cloneState.searchSetting,
        [TYPE_UPDATE_SETTING.COMMON]: cloneState.commonSetting
      };
      const menuItem = flattenMenu(mapMenuByType[updateType]).find(item => item.id === id);
      if (menuItem) {
        menuItem.children = children;
      }
      return {
        ...cloneState
      };
    }

    case UPDATE_WHOLE_MENU_SETTING: {
      const { children, updateType } = action?.payload;
      const cloneState = _.cloneDeep(state);
      const mapMenuByType = {
        [TYPE_UPDATE_SETTING.COLLECTION]: 'collectionSetting',
        [TYPE_UPDATE_SETTING.SEARCH]: 'searchSetting',
        [TYPE_UPDATE_SETTING.COMMON]: 'commonSetting'
      };

      return {
        ...cloneState,
        [mapMenuByType[updateType]]: children
      };
    }

    case REMOVE_ELEMENT: {
      const { activeMenuItem: activeMenuItemState, updateType: updateState } = state;
      const activeMenuItem = _.get(action, 'payload.activeMenuItem') || activeMenuItemState;
      const updateType = _.get(action, 'payload.updateType') || updateState;
      const cloneState = _.cloneDeep(state);
      const mapMenuByType = {
        [TYPE_UPDATE_SETTING.COLLECTION]: cloneState.collectionSetting,
        [TYPE_UPDATE_SETTING.SEARCH]: cloneState.searchSetting,
        [TYPE_UPDATE_SETTING.COMMON]: cloneState.commonSetting
      };

      let menuItem = null;
      menuItem = flattenMenu(mapMenuByType[updateType]).find(
        item => !!(item?.children || []).find(child => child.value === activeMenuItem)
      );
      if (menuItem) {
        menuItem.children = (menuItem?.children || []).filter(
          child => child.value !== activeMenuItem
        );
      }
      return {
        ...cloneState,
        activeMenuItem: '',
        activeMenuItemDetail: null,
        updateType: ''
      };
    }

    case TOGGLE_EXPAND_FULLSCREEN: {
      const { payload } = action;
      return {
        ...state,
        isExpandFullscreen: payload
      };
    }

    case GET_THEME_SETTING_BY_ID_REQUEST:
      return {
        ...state,
        templateSetting: null,
        templateSettingLoading: true,
        templateSettingSuccess: false,
        templateSettingError: undefined
      };
    case GET_THEME_SETTING_BY_ID_SUCCESS: {
      const {
        response
        // i18n
      } = action.payload;
      const reduxFormData = response?.data?.value || {};

      const objectLabelTranslation = (response?.labelTranslation?.data || []).reduce(
        (cur, next) => {
          return {
            ...cur,
            [next.key]: next
          };
        },
        {}
      );

      return {
        ...state,
        templateName: response?.templateName || '',
        templateSetting: {
          setting: reduxFormData,
          colors: response?.data?.colors,
          typography: response?.data?.typography,
          pagination: response?.data?.pagination
        },
        templateSettingLoading: false,
        templateSettingSuccess: true,
        templateSettingError: undefined,

        firstTimeActivate: response?.data?.firstTimeActivate || false,
        previewSetting: response?.previewSetting || {},
        labelTranslation: objectLabelTranslation,
        boostThemeLib: response?.boostThemeLib,
        boostThemeLibVersion: response?.boostThemeLibVersion,
        boostThemeLibName: response?.boostThemeLib,
        linkThemeIds: response?.linkThemeIds || []
      };
    }
    case GET_THEME_SETTING_BY_ID_FAILURE:
      return {
        ...state,
        templateSetting: null,
        templateSettingLoading: false,
        templateSettingSuccess: false,
        templateSettingError: action.payload
      };
    case UPDATE_THEME_SETTING:
      const { setting } = action.payload;
      return {
        ...state,
        templateSetting: setting,
        templateSettingLoading: false,
        templateSettingSuccess: false,
        templateSettingError: undefined
      };

    // Get theme setting default value
    case GET_THEME_SETTING_DEFAULT_VALUE_BY_ID_REQUEST:
      return {
        ...state,
        themeSettingDefaultValue: null,
        themeSettingDefaultThemeValue: null,
        themeSettingDefaultValueLoading: true,
        themeSettingDefaultValueSuccess: false,
        themeSettingDefaultValueError: undefined
      };
    case GET_THEME_SETTING_DEFAULT_VALUE_BY_ID_SUCCESS: {
      const { data: response } = action.payload;
      const { boostThemeLib } = state;
      const themeDefaultValueArr = response?.data || [];

      // Replace other theme default value by theme lib template
      const defaultValue = themeDefaultValueArr.find(theme => {
        return theme.themeNameLib === boostThemeLib;
      });
      const defaultThemeValue = themeDefaultValueArr.find(
        theme => theme.themeNameLib === 'default'
      );

      return {
        ...state,
        themeSettingDefaultValue: defaultValue
          ? {
              value: defaultValue?.value || {},
              valueByMenu: defaultValue?.valueByMenu || {},
              colors: defaultValue?.colors || {},
              typography: defaultValue?.typography || {},
              callToActionLayout: defaultValue?.callToActionLayout || {}
            }
          : null,
        themeSettingDefaultThemeValue: {
          value: defaultThemeValue?.value || {},
          valueByMenu: defaultThemeValue?.valueByMenu || {},
          colors: defaultThemeValue?.colors || {},
          typography: defaultThemeValue?.typography || {}
        },
        themeSettingDefaultValueLoading: false,
        themeSettingDefaultValueSuccess: true,
        themeSettingDefaultValueError: undefined
      };
    }
    case GET_THEME_SETTING_DEFAULT_VALUE_BY_ID_FAILURE:
      return {
        ...state,
        themeSettingDefaultValue: null,
        themeSettingDefaultThemeValue: null,
        themeSettingDefaultValueLoading: false,
        themeSettingDefaultValueSuccess: false,
        themeSettingDefaultValueError: action.payload
      };

    // Edit theme setting
    case EDIT_THEME_SETTING_BY_ID_REQUEST:
      return {
        ...state,
        editThemeSettingLoading: true,
        editThemeSettingSuccess: false,
        editThemeSettingError: undefined
      };
    case EDIT_THEME_SETTING_BY_ID_SUCCESS: {
      const { data } = action.payload;
      const reduxFormData = data?.value || {};

      return {
        ...state,
        templateSetting: {
          setting: reduxFormData,
          colors: data?.colors,
          typography: data?.typography,
          pagination: data?.pagination
        },
        editThemeSettingLoading: false,
        editThemeSettingSuccess: true,
        editThemeSettingError: undefined
      };
    }
    case EDIT_THEME_SETTING_BY_ID_FAILURE:
      return {
        ...state,
        editThemeSettingLoading: false,
        editThemeSettingSuccess: false,
        editThemeSettingError: action.payload
      };

    case CHANGE_DEVICE:
      return { ...state, device: action.payload };

    default:
      return state;
  }
};

export default themeSettingReducers;
